import { Button, Grid, InputAdornment, Typography } from '@mui/material'
import { DesiredSchedule, ReferralTimeFrame, type EnrollmentDetails } from 'core/types'
import { initialEnrollmentDetails, saveSession, useCaregiverWizardUpdateDispatch } from 'ContextProviders/CaregiverWizard'
import { useState } from 'react'
import { toast } from 'react-toastify'
import { DatePickerWithLabel } from 'Components/DatePicker'
import { CustomLabelWithToolTip } from 'Components/CustomLabel/Index'
import { useParams } from 'react-router'
import { useScrollTop } from 'hooks/use-scrollTop'
import { schoolYearLabel, usePortalSchoolYear } from 'Pages/CaregiverPortal/usePortalSchoolYear'
import { TextFieldWithLabel } from 'Components/TextField'
import { LabelWithToolTip } from 'Components/Label/Index'

interface ServiceDateTypeSelectionProps {
  caregiverForm: EnrollmentDetails
}

export function ServiceDateTypeSelection (props: ServiceDateTypeSelectionProps): JSX.Element {
  const { isdId, isdServiceProviderId } = useParams()
  const dispatch = useCaregiverWizardUpdateDispatch()
  const [selectedTimeOption, setSelectedTimeOption] = useState<ReferralTimeFrame | null>(null)
  const [desiredSchedule, setDesiredSchedule] = useState<DesiredSchedule | null>(null)
  const [selectedDate, setSelectedDate] = useState<Date | null>(null)
  const [annualIncome, setAnnualIncome] = useState<number | null>(null)
  const [householdMembers, setHouseholdMembers] = useState<number | null>(null)

  const { currentStartYear: currentSchoolYear, upcomingOpen, upcomingOnly } = usePortalSchoolYear(isdId, isdServiceProviderId)

  useScrollTop()

  const caregiverForm = props.caregiverForm

  const handleBack = async (): Promise<void> => {
    const initialForm = initialEnrollmentDetails()
    const tempForm = { ...caregiverForm, currentSubStep: 1, currentChild: initialForm.currentChild }
    const existingChild = tempForm.children.find(c => c.isCurrentlyUpdating)
    if (existingChild != null) {
      existingChild.isCurrentlyUpdating = false
      const service = existingChild.requestedServices?.find(s => s.isCurrentlyUpdating)
      if (service != null) {
        service.isCurrentlyUpdating = false
      }
    }
    await saveSession(tempForm)
    dispatch({ type: 'form', form: tempForm })
  }

  const handleContinue = async (): Promise<void> => {
    let errors = false

    if (selectedTimeOption == null || (selectedTimeOption === ReferralTimeFrame.SpecificDate && selectedDate == null)) {
      toast.error('Please select a service starting time to continue')
      errors = true
    }
    if (desiredSchedule == null) {
      toast.error('Please select a desired schedule to continue')
      errors = true
    }
    if (householdMembers == null || householdMembers === 0) {
      errors = true
      toast.error('Please specify household members')
    }
    if (annualIncome == null || annualIncome < 0) {
      errors = true
      toast.error('Please specify an annual income')
    }

    if (errors) { return }
    const tempForm = { ...caregiverForm, currentSubStep: 3 }
    const currentChild = tempForm.currentChild
    const service = currentChild.requestedServices?.find(s => s.isCurrentlyUpdating)

    if (service != null) {
      if (selectedTimeOption === ReferralTimeFrame.SpecificDate) {
        service.startDate = selectedDate
      }
      service.desiredSchedule = desiredSchedule
      service.timeFrame = selectedTimeOption
      service.householdMembers = householdMembers
      service.annualIncome = annualIncome
    }

    await saveSession(tempForm)
    dispatch({ type: 'form', form: tempForm })
  }

  return <Grid container spacing={2}>
    <Grid item xs={12}>
      <Typography variant='h5' sx={{ mb: '30px' }} component='div' data-testid="service-date-selection-prompt">
        <CustomLabelWithToolTip
          name='IF_Service_Date_Intro'
          isdId={isdId ?? 0}
          defaultLabelText='When are you looking to start this service?'
        />
      </Typography>
      <Typography variant='subtitle1' sx={{ mb: '30px' }} component='div'>
        <CustomLabelWithToolTip
          name='IF_Service_Date_Subtext'
          isdId={isdId ?? 0}
          defaultLabelText='If your children need multiple or different services, you will have the ability to add that later.'
        />
      </Typography>
    </Grid>

    {!upcomingOnly && <Grid item xs={12}>
      <Button
        variant="outlined"
        className={'wide-button' + (selectedTimeOption === ReferralTimeFrame.Immediate ? ' selected' : '')}
        data-testid='time-option-now'
        onClick={() => { setSelectedTimeOption(ReferralTimeFrame.Immediate) }}
      >
        Current School Year{schoolYearLabel(currentSchoolYear)}
      </Button>
    </Grid>}
    {upcomingOpen && <Grid item xs={12}>
      <Button
        variant="outlined"
        className={'wide-button' + (selectedTimeOption === ReferralTimeFrame.UpcomingSchoolYear ? ' selected' : '')}
        data-testid='time-option-next-school-year'
        onClick={() => { setSelectedTimeOption(ReferralTimeFrame.UpcomingSchoolYear) }}
      >
        Upcoming School Year{schoolYearLabel(currentSchoolYear == null ? null : currentSchoolYear + 1)}
      </Button>
    </Grid>}
    {upcomingOpen && !upcomingOnly && <Grid item xs={12}>
      <Button
        variant="outlined"
        className={'wide-button' + (selectedTimeOption === ReferralTimeFrame.SpecificDate ? ' selected' : '')}
        data-testid='time-option-specific-date'
        onClick={() => { setSelectedTimeOption(ReferralTimeFrame.SpecificDate) }}
      >
        Specific Date
      </Button>
    </Grid>}

    {selectedTimeOption === ReferralTimeFrame.SpecificDate &&
      <Grid item xs={12}>
        <DatePickerWithLabel
          label='Specific Date'
          name='selectedDate'
          dataTestId='serviceDatePicker'
          value={selectedDate}
          onChange={(val) => { setSelectedDate(val) } }
          FormControlProps={{ className: 'pb-0 width-100' }}
        />
      </Grid>
    }

    <Grid item xs={12} sx={{ mt: '30px' }}>
      <Typography variant='h5' component='div'>
        <CustomLabelWithToolTip
          name='IF_Service_Time_Intro'
          isdId={isdId ?? 0}
          defaultLabelText='What time of day do you desire this service?'
        />
      </Typography>
    </Grid>

    <Grid item xs={12}>
      <Button
        variant="outlined"
        className={'wide-button' + (desiredSchedule === DesiredSchedule.Morning ? ' selected' : '')}
        data-testid='schedule-option-morning'
        onClick={() => { setDesiredSchedule(DesiredSchedule.Morning) }}
      >
        Morning
      </Button>
    </Grid>
    <Grid item xs={12}>
      <Button
        variant="outlined"
        className={'wide-button' + (desiredSchedule === DesiredSchedule.Afternoon ? ' selected' : '')}
        data-testid='schedule-option-afternoon'
        onClick={() => { setDesiredSchedule(DesiredSchedule.Afternoon) }}
      >
        Afternoon
      </Button>
    </Grid>
    <Grid item xs={12}>
      <Button
        variant="outlined"
        className={'wide-button' + (desiredSchedule === DesiredSchedule.Evening ? ' selected' : '')}
        data-testid='schedule-option-evening'
        onClick={() => { setDesiredSchedule(DesiredSchedule.Evening) }}
      >
        Evening
      </Button>
    </Grid>
    <Grid item xs={12}>
      <Button
        variant="outlined"
        className={'wide-button' + (desiredSchedule === DesiredSchedule.AllDay ? ' selected' : '')}
        data-testid='schedule-option-all-day'
        onClick={() => { setDesiredSchedule(DesiredSchedule.AllDay) }}
      >
        All Day
      </Button>
    </Grid>

    <Grid item xs={12} sx={{ mt: '30px' }}>
      <Typography variant='h5' component='div'>
        <CustomLabelWithToolTip
          name=''
          isdId={isdId ?? 0}
          defaultLabelText='Tell us a little about your earnings'
        />
      </Typography>
    </Grid>

    <Grid item xs={12} sm={6} md={12} lg={6}>
      <TextFieldWithLabel
        dataTestId='annualIncome'
        showRequiredText={annualIncome == null || annualIncome === 0}
        required={annualIncome == null || annualIncome === 0}
        labelContent={<LabelWithToolTip
          labelText='Annual Income'
          toolTipText='This field is for annual income'
        />}
        startAdornment={<InputAdornment position="start">$</InputAdornment>}
        name='annualIncome'
        onChange={(e) => {
          setAnnualIncome(Number(e.target.value))
        }}
        value={annualIncome}
        type='number'
      />
    </Grid>
    <Grid item xs={12} sm={6} md={12} lg={6}>
      <TextFieldWithLabel
        showRequiredText={householdMembers == null || householdMembers === 0}
        required={householdMembers == null || householdMembers === 0}
        labelContent={<LabelWithToolTip
          labelText='Household Members'
          toolTipText='This field is for the number of household members'
        />}
        name='householdMembers'
        onChange={(e) => {
          setHouseholdMembers(Number(e.target.value))
        }}
        dataTestId='householdMembers'
        value={householdMembers}
        type='number'
      />
    </Grid>

    <Grid item xs={6} sx={{ mb: '20px', mt: '40px' }}>
      <Button
        name='backButton'
        className='back-button'
        data-testid='back-button'
        onClick={handleBack}
        variant='outlined'
      >
        Back
      </Button>
    </Grid>
    <Grid item xs={6} sx={{ mb: '20px', mt: '40px' }}>
      <Button
        name='continueButton'
        className='footer-button'
        data-testid='continue-button'
        onClick={handleContinue}
        variant='contained'
      >
        Continue
      </Button>
    </Grid>
  </Grid>
}
